import React, { Fragment, useRef, useEffect } from 'react';
import { Trans } from 'react-i18next';

import { ColumnPopupPropTypes } from 'src/components/DataGrid/ColumnPopup/propTypes';
import { setSorting } from 'src/utils/data/sorting';
import { userAuthorization } from 'src/utils/auth';
import { useSetQueryParams } from 'src/hooks/utils';
import ColumnPopupFilterCompany from 'src/components/DataGrid/ColumnPopup/ColumnPopupFilters/ColumnPopupFilterCompany';
import ColumnPopupFilterDate from 'src/components/DataGrid/ColumnPopup/ColumnPopupFilters/ColumnPopupFilterDate';
import ColumnPopupFilterList from 'src/components/DataGrid/ColumnPopup/ColumnPopupFilters/ColumnPopupFilterList';
import ColumnPopupFilterText from 'src/components/DataGrid/ColumnPopup/ColumnPopupFilters/ColumnPopupFilterText';
import s from 'src/components/DataGrid/ColumnPopup/ColumnPopup.styles';

const ColumnPopup = props => {
  const {
    column,
    currentOrder,
    filter,
    filterType,
    filters,
    name,
    onClose,
    orderColumn,
    refetch,
    setFilters: setFiltersReal,
    ...rest
  } = props;

  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const onSort = direction => {
    useSetQueryParams(window.location, { page: 1 });
    const orderData = {
      orderBy: orderColumn,
      orderDirection: direction,
    };
    refetch(orderData);
    setSorting(name, orderData);
    onClose();
  };

  const isActive = orderColumn === currentOrder.column;
  const { direction } = currentOrder;

  const mapping = {
    text: ColumnPopupFilterText,
    list: ColumnPopupFilterList,
    date: ColumnPopupFilterDate,
    company: ColumnPopupFilterCompany,
  };
  const ColumnFilter = mapping[filterType];

  const currentFilter = filters.find(
    c => c.field === filter && c.column?.label === column.label,
  );

  const onReload = newFilters => {
    const columnFilters = newFilters.map(({ column: c, field, value }) => ({
      field,
      value: (c.filterType === 'company') ? JSON.stringify(value.ids) : JSON.stringify(value),
    }));
    refetch({ columnFilters });
  };

  const setFilters = newFilters => {
    setFiltersReal(newFilters);
    onReload(newFilters);
  };

  const onFilterValueChange = value => {
    const newFilters = [...filters];
    const index = newFilters.findIndex(
      c => c.field === filter && c.column.label === column.label,
    );
    if (index === -1) {
      newFilters.push({
        field: filter,
        value,
        column,
      });
    } else {
      newFilters[index].value = value;
    }
    setFilters(newFilters);
  };

  const auth = userAuthorization();

  return (
    <s.ColumnPopup ref={ref}>
      {orderColumn && (
        <Fragment>
          <s.ColumnSort
            active={isActive && direction === 'asc'}
            hasActive={isActive}
            onClick={() => onSort('asc')}
          >
            <s.ColumnSortIcon name="sort-a-z" />
            {(filterType !== 'date') ? (
              <Trans>SORT_A_Z</Trans>
            ) : (
              <Trans>SORT_A_Z_DATE</Trans>
            )}
          </s.ColumnSort>
          <s.ColumnSort
            active={isActive && direction === 'desc'}
            hasActive={isActive}
            onClick={() => onSort('desc')}
          >
            <s.ColumnSortIcon name="sort-z-a" />
            {(filterType !== 'date') ? (
              <Trans>SORT_Z_A</Trans>
            ) : (
              <Trans>SORT_Z_A_DATE</Trans>
            )}
          </s.ColumnSort>
        </Fragment>
      )}
      {(name !== 'JobsGrid') && filter && (!auth.isFactoryUser || auth.isAdmin ) && (
        <s.FiltersWrapper>
          <s.FiltersHeader>
            <Trans>FILTER</Trans>
          </s.FiltersHeader>
          <ColumnFilter
            {...rest}
            column={column}
            onChange={newValue => onFilterValueChange(newValue)}
            onClose={onClose}
            value={(currentFilter || { value: [] }).value}
          />
        </s.FiltersWrapper>
      )}
    </s.ColumnPopup>
  );
};

ColumnPopup.propTypes = ColumnPopupPropTypes;

export default React.memo(ColumnPopup);
